import Api from "./wlgpi-api";

export default class Cart {
  constructor() {
    this.api = null;
    this.isOpen = false;
    this.isLoading = false;
    this.isAddingToCart = false;
    this.addItemError = null;
    this.checkoutUrl = "";
    this.items = [];
    this.totalVatExcludedFormatted = 0;
    this.totalVatIncludedFormatted = 0;
    this.lang = null;
    this.orderflowBaseUrl = this.getOrderflowBaseUrl();
    this.setupEventListeners();
  }

  async init(homeUrl, nonce, lang = null) {
    this.api = new Api("wlgpi/cartapi", homeUrl, nonce, lang);
    this.lang = lang;
    await this.refreshCart();
  }

  setupEventListeners() {
    // Smooth rapid resize applies into one animation, not visible tremors.
    if (!document.getElementById("wlgpi-cart-iframe-styles")) {
      const style = document.createElement("style");
      style.id = "wlgpi-cart-iframe-styles";
      style.textContent =
        "iframe.wlgpi-cart-iframe { transition: min-height 400ms ease-out; }";
      document.head.appendChild(style);
    }

    // Refresh cart after adding an item to it using SPA
    document.addEventListener("DOMContentLoaded", () => {
      if (
        typeof __WS_New_Order__ !== "undefined" &&
        "hooks" in __WS_New_Order__
      ) {
        __WS_New_Order__.hooks.cartItemsCount.subscribe(
          async (newCartItemsCount) => {
            if (typeof Alpine !== "undefined") {
              Alpine.nextTick(() => {
                Alpine.store("cart").refreshCart();
              });
            } else {
              await this.refreshCart();
            }
          }
        );
      }
    });

    // Cart iframe is posting messages on cart changes to parent window
    // Listen to these messages and refresh our cart as well
    window.addEventListener("message", async (event) => {
      // Handle resize messages from iframe
      if (event.data.type === "resize") {
        // Find the cart iframe that sent the message
        const cartIframe = this.getCartIframe();
        const sourceIframe =
          cartIframe && cartIframe.contentWindow === event.source
            ? cartIframe
            : null;

        if (sourceIframe && event.data.newHeight) {
          // Safety cap to stop the child's 100vh + padding runaway loop.
          const height = Math.min(
            event.data.newHeight,
            Math.max(5000, window.innerHeight * 5)
          );
          // Trim 1px: child-reported height can round up vs the rendered
          // viewport at fractional zoom, leaving a white seam on dark bg.
          const trimmedHeight = Math.max(0, height - 1);
          // Hysteresis: the child re-measures while our 400ms min-height
          // transition is still animating and reports heights ~1px lower
          // each cycle; applying them ratchets the iframe down for seconds
          // ("slowly shrinking window"). Absorb sub-threshold deltas - not
          // applying them leaves the child viewport unchanged, so its
          // observer stops firing and the loop dies.
          const RESIZE_TOLERANCE_PX = 4;
          const lastApplied = Number(sourceIframe.dataset.iframeHeight);
          if (
            !Number.isFinite(lastApplied) ||
            Math.abs(trimmedHeight - lastApplied) >= RESIZE_TOLERANCE_PX
          ) {
            sourceIframe.dataset.iframeHeight = trimmedHeight;
            sourceIframe.style.minHeight = `${trimmedHeight}px`;
          }

          // Skip min-width: iframe already fills its container (width=100%),
          // and applying child-reported width creates a scrollbar-toggle loop.
          sourceIframe.style.removeProperty("min-width");
          delete sourceIframe.dataset.iframeWidth;
        }
      }

      // Handle resetSize messages from iframe
      if (event.data.type === "resetSize") {
        // Find the cart iframe that sent the message
        const cartIframe = this.getCartIframe();
        const sourceIframe =
          cartIframe && cartIframe.contentWindow === event.source
            ? cartIframe
            : null;

        if (sourceIframe) {
          // Remove min-height and min-width styles
          sourceIframe.style.removeProperty("min-height");
          sourceIframe.style.removeProperty("min-width");

          // Clear stored dimensions from data attributes
          delete sourceIframe.dataset.iframeHeight;
          delete sourceIframe.dataset.iframeWidth;
        }
      }

      // Handle cart change messages
      if (event.data.type === "cartChange") {
        if (typeof Alpine !== "undefined") {
          Alpine.nextTick(() => {
            Alpine.store("cart").refreshCart();
          });
        } else {
          await this.refreshCart();
        }
      }

      // Handle URL change messages from iframe SPA router
      if (event.data.type === "urlChange" && event.data.newUrl) {
        const cartIframe = this.getCartIframe();
        const isFromCartIframe =
          cartIframe && cartIframe.contentWindow === event.source;

        if (isFromCartIframe) {
          const baseUrl = this.getCartPageUrl();
          if (baseUrl) {
            const url = baseUrl + event.data.newUrl;
            history.replaceState(null, "", url);
            // Emit replacestate event for other scripts that depend on URL changes
            window.dispatchEvent(new Event("replacestate"));
          }
        }
      }
    });

    // Handle window resize - remove and reapply dimensions.
    // Ignore width diffs smaller than a scrollbar (~15-17px on Windows/Linux
    // with classic scrollbars): the applied min-height can make the page
    // overflow and toggle the scrollbar, which fires `resize`; if we invalidate
    // on that, the iframe collapses -> scrollbar disappears -> another resize
    // fires, and we end up oscillating ("vibrating" iframe).
    const SCROLLBAR_WIDTH_THRESHOLD = 20;
    let lastKnownWidth = window.innerWidth;
    let resizeTimeout;
    window.addEventListener("resize", () => {
      const currentWidth = window.innerWidth;
      if (Math.abs(currentWidth - lastKnownWidth) < SCROLLBAR_WIDTH_THRESHOLD) {
        return;
      }
      lastKnownWidth = currentWidth;

      clearTimeout(resizeTimeout);

      // Immediately remove min-width/min-height on resize start
      const cartIframe = this.getCartIframe();
      if (cartIframe && cartIframe.dataset.iframeHeight) {
        cartIframe.style.removeProperty("min-height");
        cartIframe.style.removeProperty("min-width");
      }

      // Reapply dimensions after resize ends
      resizeTimeout = setTimeout(() => {
        if (cartIframe) {
          if (cartIframe.dataset.iframeHeight) {
            cartIframe.style.minHeight = `${cartIframe.dataset.iframeHeight}px`;
          }
          if (cartIframe.dataset.iframeWidth) {
            cartIframe.style.minWidth = `${cartIframe.dataset.iframeWidth}px`;
          }
        }
      }, 250);
    });
  }

  async refreshCart() {
    this.isLoading = true;
    const cart = await this.api.get("cart").then((response) => {
      if (response.error) {
        console.error("Error refreshing cart:", response.error);
      }
      return response;
    });

    this.setVariables(cart);
    this.checkoutUrl = cart.checkoutUrl ?? "";
    this.isLoading = false;

    return cart;
  }

  async removeItem(id) {
    this.isLoading = true;
    const cart = await this.api.delete("remove-item", { id });

    if (cart.error) {
      console.error("Error removing item:", cart.error);
      this.isAddingToCart = false;
      this.isLoading = false;
      return cart;
    }

    this.setVariables(cart);
    this.isLoading = false;
    return cart;
  }

  async addItem(product, propertiesJson, period, source = "") {
    this.isLoading = true;
    this.isAddingToCart = true;
    this.addItemError = null;
    let properties = JSON.parse(propertiesJson);

    // Store the current cart items before adding new ones
    const previousItems = [...this.items];

    const requestData = {
      product,
      properties,
      period,
    };

    const cart = await this.api.post("add-item", {}, {}, requestData);

    if (cart.error) {
      console.error("Error adding item:", cart.error);
      this.addItemError = {
        product,
        properties,
        source,
        message: cart.error.message || "",
      };
      this.isAddingToCart = false;
      this.isLoading = false;
      return cart;
    }

    this.setVariables(cart);
    this.isAddingToCart = false;
    this.isLoading = false;

    document.body.dispatchEvent(
      new CustomEvent("wlgpi_after_added_to_cart", {
        detail: { requestData: requestData, responseData: cart },
      })
    );

    // Pass both current and previous items to identify new ones
    this.redirectToCheckout(cart, previousItems);

    return cart;
  }

  redirectToCheckout(cart, previousItems = []) {
    const cartItems = cart.items;
    if (cartItems.length > 0) {
      // Find newly added items by comparing with previous items
      const newItems = cartItems.filter(
        (item) => !previousItems.some((prevItem) => prevItem.id === item.id)
      );

      if (newItems.length > 0) {
        let redirectUrl = cart.checkoutMandatoryUrl;
        newItems.forEach((cartItem, index) => {
          const paramNames = [
            "item",
            "itemSecondary",
            "itemTertiary",
            "itemQuaternary",
            "itemQuinary",
          ];
          const paramName = paramNames[index] || `itemN${index + 1}`;
          redirectUrl +=
            index === 0
              ? `?${paramName}=${cartItem.id}`
              : `&${paramName}=${cartItem.id}`;
        });
        window.location.href = redirectUrl;
      }
    }
  }

  setVariables(cart) {
    this.items = cart.items ?? [];
    this.totalVatExcludedFormatted = cart.totalVatExcludedFormatted ?? 0;
    this.totalVatIncludedFormatted = cart.totalVatIncludedFormatted ?? 0;
    this.notifications = cart.notifications ?? {};
  }

  getOrderflowBaseUrl() {
    // Get Orderflow base URL from wlgpi_params or fallback
    return window.wlgpi_params?.orderflow_base_url || "";
  }

  getCartPageUrl() {
    // Get cart page URL from wlgpi_params or fallback
    return window.wlgpi_params?.cart_page_url || "";
  }

  getCartIframe() {
    // Find the cart iframe using the Orderflow base URL
    // Make sure orderflowBaseUrl is not empty
    if (!this.orderflowBaseUrl) {
      return null;
    }
    return document.querySelector(`iframe[src*="${this.orderflowBaseUrl}"]`);
  }
}
